// The Scene authoring format (Order 16.5) — a location as a single loadable
// record. One JSON file carries everything that made the alley a place: the
// background plate and how the street seats against it, the oblique ground plane,
// which light skin dresses it, the props (including the ones that are also
// lights), the wet ground it reflects in, where actors spawn, and where it hands
// off to the next location. The engine loads and validates one of these; nothing
// about the location is hardcoded in a Scene class any more.
//
// WHY THIS LIVES IN ARCADE, NOT anim-schema. anim-schema is the shared,
// renderer-agnostic animation contract (deform timelines, puppets, the Marionette
// film "Scene") that Marionette and the Compositor both import — it holds "NO
// Phaser, NO Pixi", and it already uses the name `Scene` for a different concept
// (a multi-puppet film composition). A belt-scroll location is the opposite of
// renderer-agnostic: every field here is defined against arcade's own projection
// (`game/ground.ts`) and its own presentation records — the plate tint is a
// Phaser tint, the light skin is arcade's `LightSkin`, the reflective zones carry
// arcade's `airGap`. The nearest precedent settles it: `LightSkin` is itself a
// versioned, validated, id-keyed presentation record, and it deliberately lives
// in arcade (`light/LightSkin.ts`), not anim-schema. A Scene *references* a
// LightSkin, so it belongs where LightSkin belongs. We follow anim-schema's
// CONVENTIONS (a discriminator string, a `validateScene()` that returns problems,
// a hard failure on load) without moving into its PACKAGE.
//
// THE ONE EXCEPTION, cut the other way (Order 3.9984): the PERSPECTIVE GROUND.
// A photographic plate's calibrated ground — camera height, horizon, person
// gauges, the plate's sun — is NOT arcade-specific: Marionette needs the same
// record the moment a puppet stands at depth in a plate. So `perspective` is
// anim-schema's PerspectiveGround, imported, never mirrored; arcade's own
// SceneGround keeps only what is genuinely the belt's (the oblique axis and
// its linear scale ramp) plus the coarse x bounds both modes share.
//
// COORDINATE DISCIPLINE (Order 16.5 constraint). Anything on the walkable ground
// plane is authored in GROUND coordinates (x along the street, z into it) — the
// water, the spawns, the exits. Screen is `ground.ts`'s output and never anyone's
// input, so no ground-plane field is ever a screen pixel. Props are the one
// exception, and only because they are NOT ground objects: a marquee hangs on the
// wall of the plate, so it is placed directly on the picture plane (world x, world
// y), the same space the plate and the lights live in — never projected from the
// ground. The reflection's mirror plane is likewise NOT here: it is the ground
// under each actor's own feet (`groundScreenY`), never a Scene-authored `planeY`.

import {
  validatePerspectiveGround,
  type PerspectiveGround,
} from '@gooddesign/anim-schema/ground-math';

export const SCENE_SCHEMA = 'gooddesign.arcade.scene/v0';

/** The whole location as one record. Mirrors `deform-clip-set/v0`'s shape: a
 *  `schema` discriminator, an id/name, then grouped sub-records. */
export interface SceneDef {
  schema: typeof SCENE_SCHEMA;
  /** Stable handle — the id a scene is loaded by and an exit hands off to. */
  id: string;
  name: string;
  /** Surface taxonomy (arcade_authoring_spec §4): which kind of playable this
   *  scene is. 'belt' scrolls and follows; 'stage' holds a fixed-cam location;
   *  'platformer' is the pure side view (declared now, built later). Absent →
   *  derived from `camera` ('fixed' → stage, else belt), so every pre-authoring
   *  scene keeps meaning what it meant. Authoring scaffolding: the runtime
   *  still keys its behaviour off `camera`, not this. */
  type?: SceneType;
  world: SceneWorld;
  plate: ScenePlate;
  ground: SceneGround;
  /** Id of the light skin that dresses this location (see the skin registry).
   *  Referenced, never inlined — a skin is its own authoring surface. */
  lightSkin: string;
  props: SceneProp[];
  water: SceneWater;
  spawns: SceneSpawn[];
  /** Where this Scene hands off to the next, so a game can eventually be a graph
   *  of locations. Authored now; nothing consumes it yet (Order 16.5 defines the
   *  field but does not build the transition). */
  exits: SceneExit[];
  /** Camera behaviour. 'follow' (default) scrolls to track the player along x —
   *  the belt. 'fixed' holds a still framing and letterboxes the plate to fit the
   *  view — a playable-cinema shot, where the composition IS the shot and the
   *  actor recedes within a frame that never moves. */
  camera?: 'fixed' | 'follow';
  /** The shot's calibrated perspective ground (Order 3.9984) — the SHARED
   *  record (anim-schema), replacing the oblique ramp for playable-cinema
   *  shots. When present: ground.depthAngleDeg/nearScale/farScale must be
   *  ABSENT (the record owns the projection), and the shot's sun lives at
   *  perspective.sun, so `sun` below must be absent too — one home each. */
  perspective?: PerspectiveGround;
  /** The shot's sun, for matching actors' cast shadows to a photographic plate.
   *  Per-shot (each plate has its own baked sun), so it lives on the scene, not
   *  the reusable light skin. Absent → no cast shadow (the belt scenes stay as
   *  they are). Azimuth is a SCREEN direction (a shadow's rake in the picture is
   *  a screen fact); elevation is the sun's height (low → long shadow).
   *  PERSPECTIVE scenes author this inside `perspective.sun` instead. */
  sun?: SceneSun;
  /** Ground regions the plate already has in shadow. A figure standing in one
   *  sinks into it (darkens) and stops casting its own. GROUND coords (x, z),
   *  like the reflective zones. Absent → the whole ground is sunlit. */
  shade?: SceneShadeZone[];
  /** The walkable ground traced as a polygon, GROUND (x, z) — the pavement by
   *  eye, the building line and the tree bed carved out. Movement clamps inside
   *  it (game/ground.ts clampWalk). Absent → the whole belt rectangle
   *  (xMin..xMax × 0..depthPx) stays walkable, exactly as before Order 16.6.
   *  Hand-authored today; the Shot Editor's pen tool is the future author. */
  walkable?: SceneWalkable;
  /** The scene's edit: its ordered beat timeline. Absent → one implicit played
   *  beat with no advance (the belt scenes). Must end on a played beat — a
   *  scene finishes playable and leaves through its exits. */
  beats?: SceneBeat[];
  /** Standing characters in the shot — cast, not combatants. Each names WHICH
   *  rig it is (the actor-identity field 16.5 lacked), where it stands, and
   *  where its attention already is. */
  npcs?: SceneNpcDef[];
  /** Collectable items placed in the scene (arcade_authoring_spec §9). v0 is
   *  the RECORD only — an id, a place, and the use-verbs the item declares
   *  (`throw`, `swing`, `tie`…). The behaviours behind those verbs (rig-gating,
   *  grant-ability, pickup itself) are deferred; nothing in the runtime consumes
   *  this field yet. Authoring scaffolding, so a scene can be furnished before
   *  the item machinery exists. */
  items?: SceneItem[];
}

/** Which kind of playable surface a scene is (arcade_authoring_spec §4). */
export type SceneType = 'belt' | 'stage' | 'platformer';

/** A scene's effective type: its declared `type`, else derived from the camera
 *  — a fixed-cam location IS the Stage type, the follow cam IS the Belt. The
 *  one derivation rule, kept next to the format so authoring and runtime agree. */
export function sceneType(def: Pick<SceneDef, 'type' | 'camera'>): SceneType {
  return def.type ?? (def.camera === 'fixed' ? 'stage' : 'belt');
}

/** A collectable placed on the ground, GROUND (x, z) like the spawns. Declares
 *  its use-verbs by NAME only in v0 — the verb records are the contract the
 *  deferred item machinery will bind behaviours to. */
export interface SceneItem {
  id: string;
  name: string;
  x: number;
  z: number;
  /** Use-verbs this item affords ('throw', 'swing', 'tie', …). Free strings in
   *  v0; the verb vocabulary is an open spec question (§15). */
  verbs?: string[];
}

/** A standing NPC in the scene record. Ground (x, z), like the spawns; the
 *  attention field is the declarative surface arcade_playable_cinema_v0 asks
 *  for ("an actor carries { target, attitude, intensity }") — v0 carries the
 *  target and the engagement; attitude waits for the expression library. */
export interface SceneNpcDef {
  id: string;
  /** Rig asset id, resolved by the NPC rig registry (anim/npc-clipset.ts). */
  rig: string;
  /** Character disposition (arcade_authoring_spec §8). Absent → 'neutral',
   *  which is exactly what every SceneNpcDef already is: attention/performance,
   *  no combat Brief. 'ally'/'hostile' are declared now so authoring can record
   *  the intent; the Brief machinery that makes them act is deferred, and the
   *  runtime treats every disposition as neutral until it lands. */
  disposition?: 'ally' | 'hostile' | 'neutral';
  x: number;
  z: number;
  facing?: 'left' | 'right';
  attention?: {
    /** 'player' — she is watching you before you've moved — or a ground point. */
    target: 'player' | { x: number; z: number };
    /** anim-schema engagement level. Default 'locked': a watcher, not a glancer. */
    engagement?: 'glance' | 'soft' | 'locked';
  };
}

export interface SceneWalkable {
  /** The outer ring, in order (either winding); at least 3 points. */
  points: { x: number; z: number }[];
  /** Rings carved OUT of the outer — a building footprint, a tree bed. The
   *  pen tool's subtractive second ring (Order 3.9984). Each ≥ 3 points. */
  holes?: { x: number; z: number }[][];
}

// ── Beats — the scene's edit ─────────────────────────────────────────────────
// A scene is an EDIT: an ordered timeline of beats, some you play, some you
// watch (arcade_playable_cinema_v0's third layer). A played beat is the
// interactive stage this whole engine is; a watched beat is a baked insert —
// close-up, POV, cutaway — shown full-frame, then cut back. First machinery,
// Order 16.6: enough for played → watched → played, leaving on trigger or
// completion. Absent `beats` → one implicit played beat with no advance, which
// is every scene before this Order.

export type SceneBeat = ScenePlayedBeat | SceneWatchedBeat;

export interface ScenePlayedBeat {
  kind: 'played';
  id: string;
  /** Leave this beat on TRIGGER: the player enters this ground circle and the
   *  timeline advances (optionally recording story flags). Absent → terminal:
   *  the scene plays on and leaves through its exits. */
  advance?: {
    x: number;
    z: number;
    radius: number;
    set?: string[];
  };
}

export interface SceneWatchedBeat {
  kind: 'watched';
  id: string;
  /** The baked insert — exactly one of these, path relative to public/. */
  video?: string;
  image?: string;
  /** A line riding the beat (the film pipeline bakes the lip-sync into the
   *  insert itself; the engine just lands the sound). Path relative to public/. */
  audio?: string;
  /** Leave this beat on COMPLETION: when the video ends, the audio ends and
   *  this hold has elapsed — whichever is latest. A still with no audio needs
   *  it; for anything else it is a floor. Seconds. */
  holdSeconds?: number;
}

export interface SceneSun {
  /** Screen compass of the shadow's direction: 0 = right, 90 = down (toward
   *  camera), 180 = left, 270 = up (away). */
  azimuthDeg: number;
  /** Sun height above the horizon, 1..89. Low = long shadow. */
  elevationDeg: number;
  /** Cast-shadow opacity, 0..1. */
  strength: number;
  /** Shadow edge softness, screen px, >= 0. */
  softness: number;
}

export interface SceneShadeZone {
  x0: number;
  x1: number;
  z0: number;
  z1: number;
  /** Peak shade at the zone core, 0..1 (1 = fully in shadow). */
  strength: number;
  /** Edge softness, ground px. */
  feather: number;
}

/** The level's own extent and clear colour. The camera bounds to it and the plate
 *  is stretched over it; the belt has no tilemap, so these are the only "level"
 *  numbers a location needs. */
export interface SceneWorld {
  widthPx: number;
  heightPx: number;
  /** CSS-hex clear colour behind everything, "#rrggbb". */
  bgColor: string;
}

/** The background plate and the two numbers that seat the street against THIS
 *  art. `nearY`/`depthPx` belong to the plate rather than the ground because they
 *  describe where the painted kerb falls in this specific image — swap the art
 *  and only these change (see scripts/seat-plate.mjs, which prints them). */
export interface ScenePlate {
  /** Image path relative to public/. */
  image: string;
  /** Multiplicative tint, "#rrggbb". The plate arrives at full brightness (it has
   *  no normal map and takes no dynamic light) while the actors sit in a dim night
   *  ambient; untinted, the picture reads inverted. The tint is a property of the
   *  PLATE — when the art changes, this changes with it. */
  tint: string;
  /** Screen y of the ground at the near kerb (z = 0). */
  nearY: number;
  /** Walkable depth of the street, world px along the oblique axis. */
  depthPx: number;
}

/** The ground plane's own geometry. The projection MATHS live in
 *  `game/ground.ts` and nowhere else; a Scene only supplies these numbers to
 *  it. The oblique trio is the BELT's; a scene with a `perspective` record
 *  must NOT carry it (the record owns the projection), and a scene without
 *  one must (validateScene enforces both directions). */
export interface SceneGround {
  /** Depth axis angle: away-from-camera is up-and-left at this many degrees.
   *  Belt scenes only. */
  depthAngleDeg?: number;
  /** Playable extent along the street: world px on a belt, ground units on a
   *  perspective shot. Coarse bounds — a walkable polygon does the real work. */
  xMin: number;
  xMax: number;
  /** Render scale at the near kerb and at the far one (the depth
   *  foreshortening). Presentation only — the simulation never scales. Belt
   *  scenes only; a perspective shot's size law is the record's. */
  nearScale?: number;
  farScale?: number;
}

/** A light owned by a prop, positioned as an OFFSET from the prop's own (x, y).
 *  This is the coupling the marquee needs: the sign and the key light are one
 *  fact, so moving the prop moves its light with it in a single edit. Its shape
 *  otherwise matches `LightDef` (light/LightSkin.ts), colour as hex. */
export interface ScenePropLight {
  role: 'key' | 'fill' | 'rim' | 'practical';
  /** Light position relative to the prop's (x, y), world px. */
  dx: number;
  dy: number;
  radius: number;
  color: string;
  intensity: number;
  flicker?: { amount: number; hz: number };
}

/** A world-placed decoration on the picture plane (NOT the ground plane): a sign,
 *  a hanging lamp. Placed in world (x, y) — the same space as the plate and the
 *  lights — because it is painted into the scene, not standing on the street.
 *  A prop may also BE a light source (`light`), which is how the marquee stays
 *  the visible sign and the key light at once. */
export interface SceneProp {
  /** Author label, and the handle water reflects by (`ScenePuddle.reflects`). */
  id: string;
  /** Baked texture key to draw with (v0 props reference procedural textures the
   *  boot pass bakes, e.g. 'marquee'). */
  texture: string;
  /** World px on the picture plane. */
  x: number;
  y: number;
  /** Draw order. */
  depth: number;
  light?: ScenePropLight;
}

/** The bare wet road — a reflection everywhere the street is, kerb to kerb. Its
 *  zone deliberately overhangs the walkable street and carries no feather (that is
 *  the engine's rule, from the 16.4 kerb-feather bug); a Scene only says how wet
 *  it is and how high an actor still reflects. */
export interface SceneRoadWater {
  /** Reflection opacity of the bare asphalt, 0..1. Wet, not a mirror. */
  strength: number;
  /** How high above the ground an actor still reflects, world px of h. */
  airGap: number;
}

/** A puddle: a patch of ground that both reflects strongly AND shows the marquee
 *  in the water (the shot that carries the tone). Its position and footprint are
 *  in GROUND coordinates; only the visible ellipse's on-screen height is a screen
 *  measure, because that is a drawing detail of the reflection, not a place. */
export interface ScenePuddle {
  /** Ground position of the puddle centre. */
  x: number;
  z: number;
  /** Ground x-extent of the reflective footprint, world px. Doubles as the
   *  visible ellipse's screen width (x is screen-aligned at the near kerb). */
  width: number;
  /** Ground z-extent of the reflective footprint, world px. */
  depth: number;
  /** Visible ellipse height on screen, world px (a grazing reflection is
   *  foreshortened, so this is smaller than the footprint depth). */
  screenHeight: number;
  /** Reflection opacity at the puddle core, 0..1. */
  strength: number;
  /** Edge softness of the reflective footprint, world px. */
  feather: number;
  /** How high above the ground an actor still reflects here, world px of h. */
  airGap: number;
  /** Id of the prop this puddle reflects (its emissive source). */
  reflects: string;
}

export interface SceneWater {
  road: SceneRoadWater;
  puddles: ScenePuddle[];
}

/** An actor's start, in GROUND coordinates — x along the street AND z into it.
 *  z is why this format exists: the tilemap had no way to express spawn depth, so
 *  it used to be authored in the Scene class. This is where it belongs. */
export interface SceneSpawn {
  faction: 'player' | 'enemy';
  x: number;
  z: number;
}

/** A handoff to another Scene: a ground trigger + the target id, GUARDED.
 *  Defined in 16.5 as a flat handoff; grown by 16.6 into a branch edge — the
 *  story graph's edges are exits with conditions. The player takes an exit by
 *  standing in it (branch by the body); `when` gates it on story flags, `set`
 *  records the choice. The transition is consumed by SceneRunner. */
export interface SceneExit {
  id: string;
  x: number;
  z: number;
  to: string;
  /** Trigger radius, ground px. Absent → the engine default. */
  radius?: number;
  /** Story flags that must ALL be set for this exit to arm. Absent → always. */
  when?: string[];
  /** Story flags set when the exit is taken. */
  set?: string[];
  /** Authoring lifecycle (Order 16.8): a freshly connect_passage-d exit the
   *  author has not positioned on the stage yet. Seated off the player spawn
   *  and marked unplaced at creation; the mark clears the moment the exit is
   *  positioned (any x/z edit). The dev write-back REFUSES to persist a scene
   *  with an unplaced exit, so the field never reaches disk and the runtime
   *  never meets it — a half-authored trigger stays a session fact. */
  unplaced?: boolean;
}

// ── Colour helpers ───────────────────────────────────────────────────────────
// Scene JSON authors colours as "#rrggbb" strings (readable, and JSON has no hex
// literal); the engine wants 0xRRGGBB numbers. Kept here so the loader and the
// validator agree on what a valid colour is.

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

export function isHexColor(s: string): boolean {
  return HEX_COLOR.test(s);
}

/** "#rrggbb" → 0xRRGGBB. Throws on a malformed string; validateScene catches
 *  these first, so the loader only reaches this on already-valid data. */
export function parseColor(s: string): number {
  if (!isHexColor(s)) throw new Error(`not a "#rrggbb" colour: ${JSON.stringify(s)}`);
  return parseInt(s.slice(1), 16);
}

// ── Validation ───────────────────────────────────────────────────────────────
// Structural checks a producer/consumer runs before trusting a Scene, in the
// shape of `validateDeformClipSet`: return human-readable problems, empty ===
// valid, and the loader turns a non-empty result into a HARD failure rather than
// a silently half-loaded location. Pure and registry-free — whether the named
// light skin actually exists is the loader's check (it owns the registry), not
// this function's.

const ROLES = new Set(['key', 'fill', 'rim', 'practical']);
const FACTIONS = new Set(['player', 'enemy']);

export function validateScene(scene: SceneDef): string[] {
  const p: string[] = [];
  const num = (v: unknown, path: string) => {
    if (typeof v !== 'number' || !Number.isFinite(v)) { p.push(`${path} must be a finite number (got ${JSON.stringify(v)})`); return false; }
    return true;
  };
  const unit = (v: unknown, path: string) => { if (num(v, path) && ((v as number) < 0 || (v as number) > 1)) p.push(`${path} must be in 0..1 (got ${v})`); };
  const color = (v: unknown, path: string) => { if (typeof v !== 'string' || !isHexColor(v)) p.push(`${path} must be a "#rrggbb" colour (got ${JSON.stringify(v)})`); };
  const str = (v: unknown, path: string) => { if (typeof v !== 'string' || v.length === 0) p.push(`${path} must be a non-empty string`); };
  const strList = (v: unknown, path: string, of: string) => {
    if (v === undefined) return;
    if (!Array.isArray(v)) { p.push(`${path} must be an array of ${of}`); return; }
    v.forEach((f, i) => str(f, `${path}[${i}]`));
  };
  const flagList = (v: unknown, path: string) => strList(v, path, 'flag names');

  if (scene.schema !== SCENE_SCHEMA) p.push(`schema is '${scene.schema}', expected '${SCENE_SCHEMA}'`);
  str(scene.id, 'id');
  str(scene.name, 'name');
  str(scene.lightSkin, 'lightSkin');

  // world
  if (scene.world) {
    if (num(scene.world.widthPx, 'world.widthPx') && scene.world.widthPx <= 0) p.push('world.widthPx must be > 0');
    if (num(scene.world.heightPx, 'world.heightPx') && scene.world.heightPx <= 0) p.push('world.heightPx must be > 0');
    color(scene.world.bgColor, 'world.bgColor');
  } else p.push('world is required');

  // plate
  if (scene.plate) {
    str(scene.plate.image, 'plate.image');
    color(scene.plate.tint, 'plate.tint');
    num(scene.plate.nearY, 'plate.nearY');
    if (num(scene.plate.depthPx, 'plate.depthPx') && scene.plate.depthPx <= 0) p.push('plate.depthPx must be > 0');
  } else p.push('plate is required');

  // ground — the oblique trio is the belt's; a perspective scene must not
  // carry it (two projections configured for one scene is the two-homes bug).
  const hasPerspective = scene.perspective !== undefined;
  if (scene.ground) {
    const g = scene.ground;
    const okMin = num(g.xMin, 'ground.xMin');
    const okMax = num(g.xMax, 'ground.xMax');
    if (okMin && okMax && g.xMin >= g.xMax) p.push('ground.xMin must be < ground.xMax');
    if (hasPerspective) {
      for (const key of ['depthAngleDeg', 'nearScale', 'farScale'] as const) {
        if (g[key] !== undefined) p.push(`ground.${key} must be absent on a perspective scene — the perspective record owns the projection`);
      }
    } else {
      if (num(g.depthAngleDeg, 'ground.depthAngleDeg') && ((g.depthAngleDeg as number) <= 0 || (g.depthAngleDeg as number) >= 90)) p.push('ground.depthAngleDeg must be in (0, 90)');
      if (num(g.nearScale, 'ground.nearScale') && (g.nearScale as number) <= 0) p.push('ground.nearScale must be > 0');
      if (num(g.farScale, 'ground.farScale') && (g.farScale as number) <= 0) p.push('ground.farScale must be > 0');
    }
  } else p.push('ground is required');

  // perspective — validated by its own home's validator, problems prefixed.
  if (hasPerspective) {
    for (const problem of validatePerspectiveGround(scene.perspective!)) p.push(`perspective: ${problem}`);
    if (scene.sun !== undefined) p.push('sun must be absent on a perspective scene — the shot sun lives at perspective.sun');
  }

  // props
  if (!Array.isArray(scene.props)) p.push('props must be an array');
  else scene.props.forEach((pr, i) => {
    const at = `props[${i}]`;
    str(pr.id, `${at}.id`);
    str(pr.texture, `${at}.texture`);
    num(pr.x, `${at}.x`); num(pr.y, `${at}.y`); num(pr.depth, `${at}.depth`);
    if (pr.light) {
      const l = pr.light;
      if (!ROLES.has(l.role)) p.push(`${at}.light.role must be one of key|fill|rim|practical (got ${JSON.stringify(l.role)})`);
      num(l.dx, `${at}.light.dx`); num(l.dy, `${at}.light.dy`);
      if (num(l.radius, `${at}.light.radius`) && l.radius <= 0) p.push(`${at}.light.radius must be > 0`);
      color(l.color, `${at}.light.color`);
      num(l.intensity, `${at}.light.intensity`);
      if (l.flicker) { unit(l.flicker.amount, `${at}.light.flicker.amount`); if (num(l.flicker.hz, `${at}.light.flicker.hz`) && l.flicker.hz <= 0) p.push(`${at}.light.flicker.hz must be > 0`); }
    }
  });

  // water
  if (scene.water) {
    if (scene.water.road) { unit(scene.water.road.strength, 'water.road.strength'); num(scene.water.road.airGap, 'water.road.airGap'); }
    else p.push('water.road is required');
    if (!Array.isArray(scene.water.puddles)) p.push('water.puddles must be an array');
    else scene.water.puddles.forEach((pd, i) => {
      const at = `water.puddles[${i}]`;
      num(pd.x, `${at}.x`); num(pd.z, `${at}.z`);
      if (num(pd.width, `${at}.width`) && pd.width <= 0) p.push(`${at}.width must be > 0`);
      if (num(pd.depth, `${at}.depth`) && pd.depth <= 0) p.push(`${at}.depth must be > 0`);
      if (num(pd.screenHeight, `${at}.screenHeight`) && pd.screenHeight <= 0) p.push(`${at}.screenHeight must be > 0`);
      unit(pd.strength, `${at}.strength`);
      num(pd.feather, `${at}.feather`);
      num(pd.airGap, `${at}.airGap`);
      str(pd.reflects, `${at}.reflects`);
    });
  } else p.push('water is required');

  // spawns
  if (!Array.isArray(scene.spawns)) p.push('spawns must be an array');
  else {
    scene.spawns.forEach((s, i) => {
      const at = `spawns[${i}]`;
      if (!FACTIONS.has(s.faction)) p.push(`${at}.faction must be player|enemy (got ${JSON.stringify(s.faction)})`);
      num(s.x, `${at}.x`); num(s.z, `${at}.z`);
    });
    const players = scene.spawns.filter((s) => s.faction === 'player').length;
    if (players !== 1) p.push(`exactly one player spawn is required (got ${players})`);
  }

  // exits
  if (!Array.isArray(scene.exits)) p.push('exits must be an array');
  else scene.exits.forEach((e, i) => {
    const at = `exits[${i}]`;
    str(e.id, `${at}.id`); str(e.to, `${at}.to`);
    num(e.x, `${at}.x`); num(e.z, `${at}.z`);
    if (e.radius !== undefined && num(e.radius, `${at}.radius`) && e.radius <= 0) p.push(`${at}.radius must be > 0`);
    flagList(e.when, `${at}.when`);
    flagList(e.set, `${at}.set`);
    if (e.unplaced !== undefined && typeof e.unplaced !== 'boolean') p.push(`${at}.unplaced must be a boolean if present`);
  });

  if (scene.camera !== undefined && scene.camera !== 'fixed' && scene.camera !== 'follow') {
    p.push(`camera must be 'fixed' or 'follow' if present (got ${JSON.stringify(scene.camera)})`);
  }

  if (scene.type !== undefined && scene.type !== 'belt' && scene.type !== 'stage' && scene.type !== 'platformer') {
    p.push(`type must be belt|stage|platformer if present (got ${JSON.stringify(scene.type)})`);
  }

  if (scene.sun !== undefined) {
    const s = scene.sun;
    num(s.azimuthDeg, 'sun.azimuthDeg');
    if (num(s.elevationDeg, 'sun.elevationDeg') && (s.elevationDeg < 1 || s.elevationDeg > 89)) p.push('sun.elevationDeg must be in 1..89');
    unit(s.strength, 'sun.strength');
    if (num(s.softness, 'sun.softness') && s.softness < 0) p.push('sun.softness must be >= 0');
  }

  if (scene.shade !== undefined) {
    if (!Array.isArray(scene.shade)) p.push('shade must be an array');
    else scene.shade.forEach((z, i) => {
      const at = `shade[${i}]`;
      num(z.x0, `${at}.x0`); num(z.x1, `${at}.x1`);
      num(z.z0, `${at}.z0`); num(z.z1, `${at}.z1`);
      unit(z.strength, `${at}.strength`);
      num(z.feather, `${at}.feather`);
    });
  }

  if (scene.walkable !== undefined) {
    const ring = (pts: unknown, at: string) => {
      if (!Array.isArray(pts)) { p.push(`${at} must be an array`); return; }
      if (pts.length < 3) p.push(`${at} needs at least 3 points (got ${pts.length})`);
      (pts as { x: unknown; z: unknown }[]).forEach((pt, i) => {
        num(pt.x, `${at}[${i}].x`);
        num(pt.z, `${at}[${i}].z`);
      });
    };
    ring(scene.walkable.points, 'walkable.points');
    if (scene.walkable.holes !== undefined) {
      if (!Array.isArray(scene.walkable.holes)) p.push('walkable.holes must be an array of rings');
      else scene.walkable.holes.forEach((h, i) => ring(h, `walkable.holes[${i}]`));
    }
  }

  // npcs
  if (scene.npcs !== undefined) {
    if (!Array.isArray(scene.npcs)) p.push('npcs must be an array');
    else {
      const ids = new Set<string>();
      scene.npcs.forEach((n, i) => {
        const at = `npcs[${i}]`;
        str(n.id, `${at}.id`);
        if (typeof n.id === 'string') {
          if (ids.has(n.id)) p.push(`${at}.id '${n.id}' is not unique`);
          ids.add(n.id);
        }
        str(n.rig, `${at}.rig`);
        if (n.disposition !== undefined && n.disposition !== 'ally' && n.disposition !== 'hostile' && n.disposition !== 'neutral') {
          p.push(`${at}.disposition must be ally|hostile|neutral if present (got ${JSON.stringify(n.disposition)})`);
        }
        num(n.x, `${at}.x`); num(n.z, `${at}.z`);
        if (n.facing !== undefined && n.facing !== 'left' && n.facing !== 'right') {
          p.push(`${at}.facing must be 'left' or 'right' if present (got ${JSON.stringify(n.facing)})`);
        }
        if (n.attention !== undefined) {
          const t = n.attention.target;
          if (t !== 'player' && !(typeof t === 'object' && t !== null)) {
            p.push(`${at}.attention.target must be 'player' or a ground point {x, z}`);
          } else if (t !== 'player') {
            num(t.x, `${at}.attention.target.x`);
            num(t.z, `${at}.attention.target.z`);
          }
          const e = n.attention.engagement;
          if (e !== undefined && e !== 'glance' && e !== 'soft' && e !== 'locked') {
            p.push(`${at}.attention.engagement must be glance|soft|locked (got ${JSON.stringify(e)})`);
          }
        }
      });
    }
  }

  // items
  if (scene.items !== undefined) {
    if (!Array.isArray(scene.items)) p.push('items must be an array');
    else {
      const ids = new Set<string>();
      scene.items.forEach((it, i) => {
        const at = `items[${i}]`;
        str(it.id, `${at}.id`);
        if (typeof it.id === 'string') {
          if (ids.has(it.id)) p.push(`${at}.id '${it.id}' is not unique`);
          ids.add(it.id);
        }
        str(it.name, `${at}.name`);
        num(it.x, `${at}.x`); num(it.z, `${at}.z`);
        strList(it.verbs, `${at}.verbs`, 'use-verb names');
      });
    }
  }

  // beats
  if (scene.beats !== undefined) {
    if (!Array.isArray(scene.beats)) p.push('beats must be an array');
    else {
      if (scene.beats.length === 0) p.push('beats must not be empty if present (omit the field instead)');
      const ids = new Set<string>();
      scene.beats.forEach((b, i) => {
        const at = `beats[${i}]`;
        str(b.id, `${at}.id`);
        if (typeof b.id === 'string') {
          if (ids.has(b.id)) p.push(`${at}.id '${b.id}' is not unique`);
          ids.add(b.id);
        }
        if (b.kind === 'played') {
          if (b.advance !== undefined) {
            num(b.advance.x, `${at}.advance.x`);
            num(b.advance.z, `${at}.advance.z`);
            if (num(b.advance.radius, `${at}.advance.radius`) && b.advance.radius <= 0) p.push(`${at}.advance.radius must be > 0`);
            flagList(b.advance.set, `${at}.advance.set`);
          }
        } else if (b.kind === 'watched') {
          const media = [b.video, b.image].filter((m) => m !== undefined);
          if (media.length !== 1) p.push(`${at} must carry exactly one of video/image (got ${media.length})`);
          if (b.video !== undefined) str(b.video, `${at}.video`);
          if (b.image !== undefined) str(b.image, `${at}.image`);
          if (b.audio !== undefined) str(b.audio, `${at}.audio`);
          if (b.holdSeconds !== undefined && num(b.holdSeconds, `${at}.holdSeconds`) && b.holdSeconds <= 0) p.push(`${at}.holdSeconds must be > 0`);
        } else {
          p.push(`${at}.kind must be 'played' or 'watched' (got ${JSON.stringify((b as { kind?: unknown }).kind)})`);
        }
      });
      const last = scene.beats[scene.beats.length - 1];
      if (last && last.kind !== 'played') p.push('the last beat must be played — a scene finishes playable and leaves through its exits');
    }
  }

  return p;
}
